import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Plot from 'react-plotly.js';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import MethodCard from './components/MethodCard';
import CancelButton from './components/CancelButton';
import apiClient from '../../api/client';
import WorkbenchContextBar from '../../components/workbench/WorkbenchContextBar';
import ToolSwitcher from '../../components/workbench/ToolSwitcher';
import { useToolStep } from '../../hooks/useWorkbenchContext';

// ---------------------------------------------------------------------------
// Model metadata (from R source inspection)
// ---------------------------------------------------------------------------

interface ModelInfo {
  name: string;           // filename sent to hub
  label: string;          // human-friendly name
  description: string;
  category: 'PK' | 'PKPD' | 'Population';
  params: { key: string; label: string; value: number; unit: string }[];
  dosing: string;
  columns: string[];      // expected CSV columns for charting
}

const MODELS: ModelInfo[] = [
  {
    name: 'mrgsolve_1cpt_iv_bolus.R', label: '1-Compartment IV Bolus',
    description: 'Single-compartment model with first-order elimination after IV bolus.',
    category: 'PK',
    params: [
      { key: 'CL', label: 'Clearance', value: 1, unit: 'L/h' },
      { key: 'V', label: 'Volume', value: 20, unit: 'L' },
    ],
    dosing: '100 mg IV bolus',
    columns: ['CP'],
  },
  {
    name: 'mrgsolve_1cpt_oral_fo.R', label: '1-Compartment Oral (First-Order)',
    description: 'Single-compartment with first-order absorption (oral dosing).',
    category: 'PK',
    params: [
      { key: 'CL', label: 'Clearance', value: 1, unit: 'L/h' },
      { key: 'V', label: 'Volume', value: 20, unit: 'L' },
      { key: 'KA', label: 'Absorption rate', value: 1, unit: '1/h' },
    ],
    dosing: '100 mg oral',
    columns: ['CP'],
  },
  {
    name: 'mrgsolve_2cpt_iv_bolus.R', label: '2-Compartment IV Bolus',
    description: 'Two-compartment model with central and peripheral distribution.',
    category: 'PK',
    params: [
      { key: 'CL', label: 'Clearance', value: 1, unit: 'L/h' },
      { key: 'V1', label: 'Central volume', value: 10, unit: 'L' },
      { key: 'Q', label: 'Intercompartmental CL', value: 2, unit: 'L/h' },
      { key: 'V2', label: 'Peripheral volume', value: 20, unit: 'L' },
    ],
    dosing: '100 mg IV bolus',
    columns: ['CP'],
  },
  {
    name: 'mrgsolve_iv_infusion.R', label: 'IV Infusion',
    description: 'Single-compartment with constant-rate IV infusion.',
    category: 'PK',
    params: [
      { key: 'CL', label: 'Clearance', value: 1, unit: 'L/h' },
      { key: 'V', label: 'Volume', value: 20, unit: 'L' },
    ],
    dosing: '100 mg IV infusion (rate 50 mg/h)',
    columns: ['CP'],
  },
  {
    name: 'mrgsolve_mm_elimination.R', label: 'Michaelis-Menten Elimination',
    description: 'Non-linear (saturable) elimination kinetics.',
    category: 'PK',
    params: [
      { key: 'VMAX', label: 'Max elimination rate', value: 10, unit: 'mg/h' },
      { key: 'KM', label: 'Michaelis constant', value: 2, unit: 'mg/L' },
      { key: 'V', label: 'Volume', value: 20, unit: 'L' },
    ],
    dosing: '500 mg IV bolus',
    columns: ['CP'],
  },
  {
    name: 'mrgsolve_multiple_dosing_ss.R', label: 'Multiple Dosing (Steady State)',
    description: 'Oral dosing every 12 hours to achieve steady-state concentrations.',
    category: 'PK',
    params: [
      { key: 'CL', label: 'Clearance', value: 1, unit: 'L/h' },
      { key: 'V', label: 'Volume', value: 20, unit: 'L' },
      { key: 'KA', label: 'Absorption rate', value: 1, unit: '1/h' },
    ],
    dosing: '100 mg oral q12h x10 doses',
    columns: ['CP'],
  },
  {
    name: 'mrgsolve_pkpd_effect_comp_emax.R', label: 'Effect Compartment Emax',
    description: 'PK-linked effect compartment with Emax pharmacodynamic model.',
    category: 'PKPD',
    params: [
      { key: 'CL', label: 'Clearance', value: 1, unit: 'L/h' },
      { key: 'V', label: 'Volume', value: 20, unit: 'L' },
      { key: 'KE0', label: 'Effect-site equil. rate', value: 0.5, unit: '1/h' },
      { key: 'EC50', label: 'Half-max effect conc', value: 2, unit: 'mg/L' },
      { key: 'EMAX', label: 'Maximum effect', value: 100, unit: '%' },
    ],
    dosing: '100 mg IV bolus',
    columns: ['CP'],
  },
  {
    name: 'mrgsolve_pkpd_indirect_response.R', label: 'Indirect Response Model',
    description: 'Indirect response model — drug inhibits the production of a biomarker.',
    category: 'PKPD',
    params: [
      { key: 'CL', label: 'Clearance', value: 1, unit: 'L/h' },
      { key: 'V', label: 'Volume', value: 20, unit: 'L' },
      { key: 'KIN', label: 'Production rate', value: 100, unit: '1/h' },
      { key: 'KOUT', label: 'Degradation rate', value: 2, unit: '1/h' },
      { key: 'IC50', label: 'Half-max inhibitory conc', value: 2, unit: 'mg/L' },
    ],
    dosing: '100 mg IV bolus',
    columns: ['CP'],
  },
  {
    name: 'mrgsolve_pop_iiv_oral.R', label: 'Population PK (IIV)',
    description: 'Population simulation with inter-individual variability (100 subjects).',
    category: 'Population',
    params: [
      { key: 'TVCL', label: 'Typical CL', value: 1, unit: 'L/h' },
      { key: 'TVV', label: 'Typical V', value: 20, unit: 'L' },
      { key: 'TVKA', label: 'Typical KA', value: 1, unit: '1/h' },
    ],
    dosing: '100 mg oral (100 subjects)',
    columns: ['CP'],
  },
];

// ---------------------------------------------------------------------------
// Fit models (maps to backend /pkpd/fit)
// ---------------------------------------------------------------------------

interface FitModelInfo {
  id: string;
  label: string;
  description: string;
  equation: string;
  params: string[];
}

const FIT_MODELS: FitModelInfo[] = [
  { id: '1cpt_iv', label: '1-Compartment IV Bolus', description: 'Monoexponential elimination', equation: 'C(t) = (Dose/V) \u00B7 exp(-ke\u00B7t)', params: ['CL', 'V'] },
  { id: '1cpt_oral', label: '1-Compartment Oral', description: 'First-order absorption + elimination', equation: 'C(t) = (Dose\u00B7KA)/(V\u00B7(KA-ke)) \u00B7 (exp(-ke\u00B7t) - exp(-KA\u00B7t))', params: ['CL', 'V', 'KA'] },
  { id: '2cpt_iv', label: '2-Compartment IV Bolus', description: 'Biexponential distribution + elimination', equation: 'C(t) = A\u00B7exp(-\u03B1t) + B\u00B7exp(-\u03B2t)', params: ['CL', 'V1', 'Q', 'V2'] },
  { id: 'iv_infusion', label: 'IV Infusion', description: 'Constant-rate infusion with first-order elimination', equation: 'C(t) = (R\u2080/CL)(1 - exp(-ke\u00B7t))', params: ['CL', 'V'] },
];

const FIT_SAMPLE_DATA = `0.5,0.8
1,2.1
2,3.5
3,3.2
4,2.8
6,2.0
8,1.3
12,0.5
24,0.05`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

interface ChartPoint {
  time: number;
  CP: number;
  [key: string]: number;
}

function parseCSV(csv: string): ChartPoint[] {
  const lines = csv.trim().split('\n');
  if (lines.length < 2) return [];
  const headers = lines[0].replace(/"/g, '').split(',');
  const timeIdx = headers.indexOf('time');
  const cpIdx = headers.indexOf('CP');
  if (timeIdx < 0 || cpIdx < 0) return [];

  const points: ChartPoint[] = [];
  for (let i = 1; i < lines.length; i++) {
    const parts = lines[i].split(',');
    const time = parseFloat(parts[timeIdx]);
    const cp = parseFloat(parts[cpIdx]);
    if (!isNaN(time) && !isNaN(cp)) {
      points.push({ time, CP: cp });
    }
  }
  return points;
}

function computePKParams(points: ChartPoint[]): { label: string; value: string }[] {
  if (points.length === 0) return [];
  const cps = points.map((p) => p.CP);
  const cmax = Math.max(...cps);
  const cmaxIdx = cps.indexOf(cmax);
  const tmax = points[cmaxIdx]?.time ?? 0;

  // AUC by trapezoidal rule
  let auc = 0;
  for (let i = 1; i < points.length; i++) {
    const dt = points[i].time - points[i - 1].time;
    auc += 0.5 * (points[i - 1].CP + points[i].CP) * dt;
  }

  // Half-life: find time where CP drops to Cmax/2 (after Tmax)
  let thalf = '\u2014';
  const halfC = cmax / 2;
  for (let i = cmaxIdx + 1; i < points.length; i++) {
    if (points[i].CP <= halfC) {
      // Linear interpolation
      const t0 = points[i - 1].time, t1 = points[i].time;
      const c0 = points[i - 1].CP, c1 = points[i].CP;
      const t = t0 + (halfC - c0) / (c1 - c0) * (t1 - t0);
      thalf = (t - tmax).toFixed(2) + ' h';
      break;
    }
  }

  // Last concentration
  const clast = cps[cps.length - 1];

  return [
    { label: 'Cmax', value: cmax.toPrecision(4) + ' mg/L' },
    { label: 'Tmax', value: tmax.toFixed(2) + ' h' },
    { label: 'AUC (trapezoidal)', value: auc.toPrecision(4) + ' mg\u00B7h/L' },
    { label: 't\u00BD', value: thalf },
    { label: 'C_last', value: clast.toPrecision(4) + ' mg/L' },
    { label: 'Data points', value: String(points.length) },
  ];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const WorkbenchPKPD: React.FC = () => {
  // Tab: 'simulate' or 'fit'
  const [activeTab, setActiveTab] = useState<'simulate' | 'fit'>('simulate');

  // --- Simulate state ---
  const [selectedModel, setSelectedModel] = useState<ModelInfo | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [chartData, setChartData] = useState<ChartPoint[]>([]);
  const [pkParams, setPkParams] = useState<{ label: string; value: string }[]>([]);
  const [pngB64, setPngB64] = useState<string | null>(null);
  const [stdout, setStdout] = useState('');
  const [error, setError] = useState('');
  const [duration, setDuration] = useState(0);
  const abortRef = useRef<AbortController | null>(null);

  // --- Fit state ---
  const [fitModel, setFitModel] = useState<FitModelInfo>(FIT_MODELS[1]); // default: oral
  const [fitRawText, setFitRawText] = useState('');
  const [fitDose, setFitDose] = useState(100);
  const [fitInfDuration, setFitInfDuration] = useState(1);
  const [fitParsedPoints, setFitParsedPoints] = useState<{ t: number; c: number }[]>([]);
  const [fitParseError, setFitParseError] = useState('');
  const [fitIsRunning, setFitIsRunning] = useState(false);
  const [fitResult, setFitResult] = useState<any>(null);
  const [fitError, setFitError] = useState('');
  const fitAbortRef = useRef<AbortController | null>(null);

  const { bailOutText, setHasSaved } = useToolStep();

  const runModel = async (model: ModelInfo) => {
    setIsRunning(true);
    setError('');
    setChartData([]);
    setPkParams([]);
    setPngB64(null);
    setStdout('');
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const resp = await apiClient.post('/v2/pkpd/run', {
        name: model.name,
      }, { signal: controller.signal });
      const data = resp.data;

      setDuration(data.duration_s || 0);
      setStdout(data.stdout || '');

      if (data.exit_code !== 0) {
        setError(data.stderr || 'Model execution failed');
        return;
      }

      if (data.csv_data) {
        const points = parseCSV(data.csv_data);
        setChartData(points);
        setPkParams(computePKParams(points));
      }

      if (data.png_b64) {
        setPngB64(data.png_b64);
      }
    } catch (err: any) {
      if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
        setError(err.response?.data?.detail || err.message || 'Run failed');
      }
    } finally {
      setIsRunning(false);
      abortRef.current = null;
    }
  };

  const selectAndRun = (model: ModelInfo) => {
    setSelectedModel(model);
    runModel(model);
  };

  // --- Fit functions ---
  const parseFitData = (text: string) => {
    setFitParseError('');
    const lines = text.trim().split('\n').filter((l) => l.trim());
    const pts: { t: number; c: number }[] = [];
    for (let idx = 0; idx < lines.length; idx++) {
      const parts = lines[idx].split(/[,\t;]+/).map((s) => s.trim());
      if (parts.length < 2) { if (idx === 0) continue; setFitParseError(`Line ${idx + 1}: need 2 columns`); return; }
      const t = parseFloat(parts[0]);
      const c = parseFloat(parts[1]);
      if (isNaN(t) || isNaN(c)) { if (idx === 0) continue; setFitParseError(`Line ${idx + 1}: non-numeric`); return; }
      pts.push({ t, c });
    }
    if (pts.length < 3) { setFitParseError('Need at least 3 data points'); return; }
    setFitParsedPoints(pts);
  };

  const runFit = async () => {
    if (fitParsedPoints.length < 3) return;
    setFitIsRunning(true);
    setFitError('');
    setFitResult(null);
    const controller = new AbortController();
    fitAbortRef.current = controller;

    try {
      const body: any = {
        model: fitModel.id,
        time: fitParsedPoints.map((p) => p.t),
        concentration: fitParsedPoints.map((p) => p.c),
        dose: fitDose,
      };
      if (fitModel.id === 'iv_infusion') body.infusion_duration = fitInfDuration;

      const resp = await apiClient.post('/v2/pkpd/fit', body, { signal: controller.signal });
      setFitResult(resp.data);
    } catch (err: any) {
      if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
        setFitError(err.response?.data?.detail || err.message || 'Fit failed');
      }
    } finally {
      setFitIsRunning(false);
      fitAbortRef.current = null;
    }
  };

  const categories = ['PK', 'PKPD', 'Population'] as const;

  return (
    <>
    <WorkbenchContextBar />
    <div style={{ display: 'flex', height: 'calc(100vh - 84px)', background: 'var(--content-bg)' }}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Status bar */}
        <div style={statusBarStyle}>
          <Link to="/hubs" style={{ color: '#f59e0b', textDecoration: 'none' }}>Toolbox</Link>
          <span style={{ color: 'var(--card-border)' }}>|</span>
          <ToolSwitcher currentToolId="pkpd" />
          <span style={{ color: 'var(--card-border)' }}>|</span>
          <span>Engine: {activeTab === 'simulate' ? 'mrgsolve (R)' : 'SciPy curve_fit'}</span>
          {selectedModel && activeTab === 'simulate' && (
            <>
              <span style={{ color: 'var(--card-border)' }}>|</span>
              <span style={{ color: '#f59e0b' }}>{selectedModel.label}</span>
            </>
          )}
          <div style={{ flex: 1 }} />
          <CancelButton onCancel={() => { abortRef.current?.abort(); fitAbortRef.current?.abort(); }} isRunning={isRunning || fitIsRunning} />
        </div>

        {/* Tab bar */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--card-border)', background: 'var(--hover-bg)' }}>
          {(['simulate', 'fit'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '10px 24px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                background: 'transparent', border: 'none',
                color: activeTab === tab ? '#f59e0b' : 'var(--text-secondary)',
                borderBottom: activeTab === tab ? '2px solid #f59e0b' : '2px solid transparent',
              }}
            >
              {tab === 'simulate' ? 'Simulate' : 'Fit Data'}
            </button>
          ))}
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: 24, display: 'flex', gap: 20 }}>
          <div style={{ flex: 1, minWidth: 0 }}>

            {/* ================================================================ */}
            {/* SIMULATE TAB */}
            {/* ================================================================ */}
            {activeTab === 'simulate' && (
              <>
                {/* Model selection */}
                {!selectedModel && (
                  <div>
                    <h2 style={h2Style}>PK/PD Models</h2>
                    <p style={descStyle}>Select a pharmacokinetic model to simulate. All models use mrgsolve (R) with pre-configured parameters and dosing regimens.</p>

                    {categories.map((cat) => {
                      const catModels = MODELS.filter((m) => m.category === cat);
                      if (catModels.length === 0) return null;
                      return (
                        <div key={cat} style={{ marginBottom: 20 }}>
                          <div style={sectionLabel}>{cat === 'PK' ? 'Pharmacokinetics' : cat === 'PKPD' ? 'PK/PD Models' : 'Population PK'}</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {catModels.map((model) => (
                              <button
                                key={model.name}
                                onClick={() => selectAndRun(model)}
                                style={modelCardStyle}
                                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = '#f59e0b'; }}
                                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--card-border)'; }}
                              >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                  <div>
                                    <div style={{ color: 'var(--text-primary)', fontSize: 14, fontWeight: 600 }}>{model.label}</div>
                                    <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 2 }}>{model.description}</div>
                                  </div>
                                  <span style={runBadge}>Run</span>
                                </div>
                                <div style={{ display: 'flex', gap: 12, marginTop: 8, fontSize: 11, color: 'var(--text-secondary)' }}>
                                  <span>Dose: {model.dosing}</span>
                                  <span>Params: {model.params.map((p) => p.key).join(', ')}</span>
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Results view */}
                {selectedModel && (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                      <button onClick={() => { setSelectedModel(null); setChartData([]); setPkParams([]); setPngB64(null); setError(''); }} style={btnStyle}>
                        &larr; All Models
                      </button>
                      <h2 style={{ ...h2Style, marginBottom: 0 }}>{selectedModel.label}</h2>
                    </div>
                    <p style={descStyle}>{selectedModel.description}</p>

                    {/* Parameters table */}
                    <div style={{ marginBottom: 16 }}>
                      <div style={sectionLabel}>Model Parameters</div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {selectedModel.params.map((p) => (
                          <div key={p.key} style={paramCard}>
                            <div style={{ fontSize: 10, color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase' }}>{p.key}</div>
                            <div style={{ fontSize: 14, fontWeight: 700, fontFamily: 'monospace', color: '#f59e0b' }}>{p.value}</div>
                            <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{p.unit}</div>
                          </div>
                        ))}
                        <div style={paramCard}>
                          <div style={{ fontSize: 10, color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase' }}>Dosing</div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', marginTop: 2 }}>{selectedModel.dosing}</div>
                        </div>
                      </div>
                    </div>

                    {isRunning && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                        <div style={spinnerStyle} /><span style={{ color: '#f59e0b' }}>Running mrgsolve simulation...</span>
                      </div>
                    )}
                    {error && <div style={errorStyle}>{error}</div>}

                    {/* Concentration-time chart */}
                    {chartData.length > 0 && (
                      <div style={{ marginBottom: 20 }}>
                        <div style={sectionLabel}>Concentration-Time Profile</div>
                        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 12, padding: '16px 12px 8px 0' }}>
                          <ResponsiveContainer width="100%" height={320}>
                            <LineChart data={chartData}>
                              <CartesianGrid strokeDasharray="3 3" stroke="var(--card-border)" />
                              <XAxis
                                dataKey="time"
                                label={{ value: 'Time (h)', position: 'insideBottom', offset: -4, style: { fill: 'var(--text-muted)', fontSize: 12 } }}
                                tick={{ fill: 'var(--text-secondary)', fontSize: 11 }}
                                stroke="var(--card-border)"
                              />
                              <YAxis
                                label={{ value: 'Cp (mg/L)', angle: -90, position: 'insideLeft', offset: 10, style: { fill: 'var(--text-muted)', fontSize: 12 } }}
                                tick={{ fill: 'var(--text-secondary)', fontSize: 11 }}
                                stroke="var(--card-border)"
                              />
                              <Tooltip
                                contentStyle={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 8, fontSize: 12 }}
                                labelStyle={{ color: 'var(--text-muted)' }}
                                formatter={(value: any) => [Number(value).toPrecision(4) + ' mg/L', 'Cp']}
                                labelFormatter={(label: any) => `t = ${label} h`}
                              />
                              <Legend wrapperStyle={{ fontSize: 11 }} />
                              <Line
                                type="monotone"
                                dataKey="CP"
                                stroke="#f59e0b"
                                strokeWidth={2}
                                dot={false}
                                name="Plasma concentration"
                              />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}

                    {/* PK parameters */}
                    {pkParams.length > 0 && (
                      <div style={{ marginBottom: 20 }}>
                        <div style={sectionLabel}>Computed PK Parameters</div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
                          {pkParams.map((p) => (
                            <div key={p.label} style={resultCardStyle}>
                              <div style={resultLabelStyle}>{p.label}</div>
                              <div style={resultValueStyle}>{p.value}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Execution info */}
                    {duration > 0 && (
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 12 }}>
                        Simulation completed in {duration.toFixed(2)}s
                      </div>
                    )}

                    {/* Actions */}
                    {chartData.length > 0 && (
                      <div style={{ display: 'flex', gap: 10 }}>
                        <button onClick={() => {
                          const lines = ['time,CP', ...chartData.map((p) => `${p.time},${p.CP}`)];
                          const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a'); a.href = url; a.download = `${selectedModel.name.replace('.R', '')}_results.csv`; a.click(); URL.revokeObjectURL(url);
                        }} style={primaryBtnStyle}>Download CSV</button>
                        <button onClick={() => navigator.clipboard.writeText(JSON.stringify({ model: selectedModel.label, params: pkParams, data_points: chartData.length }, null, 2))} style={btnStyle}>Copy Summary</button>
                        <button onClick={() => runModel(selectedModel)} disabled={isRunning} style={{ ...btnStyle, opacity: isRunning ? 0.5 : 1 }}>Re-run</button>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {/* ================================================================ */}
            {/* FIT DATA TAB */}
            {/* ================================================================ */}
            {activeTab === 'fit' && !fitResult && (
              <div>
                <h2 style={h2Style}>Fit Experimental Data</h2>
                <p style={descStyle}>
                  Paste observed time-concentration data and fit a PK model to estimate parameters (CL, V, KA, etc.) using non-linear least squares.
                </p>

                {/* Model selection */}
                <div style={{ marginBottom: 16 }}>
                  <div style={sectionLabel}>PK Model</div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {FIT_MODELS.map((m) => (
                      <button
                        key={m.id}
                        onClick={() => setFitModel(m)}
                        style={{
                          ...btnStyle,
                          ...(fitModel.id === m.id ? { background: 'rgba(245,158,11,0.15)', borderColor: '#f59e0b', color: '#f59e0b' } : {}),
                        }}
                      >
                        {m.label}
                      </button>
                    ))}
                  </div>
                  <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                    {fitModel.equation}
                  </div>
                </div>

                {/* Dose input */}
                <div style={{ display: 'flex', gap: 16, marginBottom: 16, alignItems: 'flex-end' }}>
                  <label style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                    Dose (mg):{' '}
                    <input type="number" value={fitDose} onChange={(e) => setFitDose(parseFloat(e.target.value) || 0)}
                      style={{ ...selectStyle, width: 80 }} />
                  </label>
                  {fitModel.id === 'iv_infusion' && (
                    <label style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                      Infusion duration (h):{' '}
                      <input type="number" value={fitInfDuration} onChange={(e) => setFitInfDuration(parseFloat(e.target.value) || 1)}
                        style={{ ...selectStyle, width: 80 }} />
                    </label>
                  )}
                </div>

                {/* Data input */}
                <div style={{ marginBottom: 16 }}>
                  <div style={sectionLabel}>Observed Data (time, concentration)</div>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    <button onClick={() => setFitRawText(FIT_SAMPLE_DATA)} style={btnStyle}>Load Sample Data</button>
                    <label style={{ ...btnStyle, cursor: 'pointer' }}>
                      Upload File
                      <input type="file" accept=".csv,.txt,.tsv" style={{ display: 'none' }}
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) f.text().then(setFitRawText); }} />
                    </label>
                  </div>
                  <textarea
                    value={fitRawText}
                    onChange={(e) => setFitRawText(e.target.value)}
                    placeholder="time,concentration&#10;0.5,0.8&#10;1,2.1&#10;2,3.5&#10;4,2.8&#10;12,0.5"
                    style={textareaStyle}
                    rows={10}
                  />
                  {fitParseError && <div style={errorStyle}>{fitParseError}</div>}
                </div>

                {/* Parse + Fit */}
                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    onClick={() => { parseFitData(fitRawText); }}
                    disabled={!fitRawText.trim()}
                    style={{ ...btnStyle, opacity: fitRawText.trim() ? 1 : 0.5 }}
                  >
                    Parse Data
                  </button>
                  {fitParsedPoints.length >= 3 && (
                    <button onClick={runFit} disabled={fitIsRunning} style={{ ...primaryBtnStyle, opacity: fitIsRunning ? 0.5 : 1 }}>
                      Fit {fitModel.label} ({fitParsedPoints.length} points)
                    </button>
                  )}
                </div>

                {fitParsedPoints.length >= 3 && !fitIsRunning && !fitResult && (() => {
                  // Check for data–model mismatch
                  const cmaxIdx = fitParsedPoints.reduce((best, pt, i) => pt.c > fitParsedPoints[best].c ? i : best, 0);
                  const hasAbsorption = cmaxIdx > 0; // concentration rises before falling → oral-like
                  const isOralModel = fitModel.id === '1cpt_oral';
                  const isIvModel = fitModel.id === '1cpt_iv' || fitModel.id === '2cpt_iv' || fitModel.id === 'iv_infusion';
                  const mismatch = (hasAbsorption && isIvModel) || (!hasAbsorption && isOralModel);

                  return (
                    <div style={{ marginTop: 12 }}>
                      {mismatch && (
                        <div style={{ padding: '10px 14px', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 8, marginBottom: 10, fontSize: 13, color: '#fbbf24' }}>
                          <span style={{ fontWeight: 600 }}>Data–model mismatch: </span>
                          {hasAbsorption && isIvModel
                            ? 'Your data shows an absorption phase (concentration rises then falls), but you selected an IV model. Consider using the 1-Compartment Oral model instead.'
                            : 'Your data shows no absorption phase (concentration is highest at the earliest timepoint), but you selected an oral model. Consider using an IV model instead.'}
                        </div>
                      )}
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                        {fitParsedPoints.length} points parsed. Ready to fit.
                      </div>
                    </div>
                  );
                })()}

                {fitIsRunning && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16 }}>
                    <div style={spinnerStyle} /><span style={{ color: '#f59e0b' }}>Fitting parameters...</span>
                  </div>
                )}
                {fitError && <div style={errorStyle}>{fitError}</div>}
              </div>
            )}

            {/* Fit results */}
            {activeTab === 'fit' && fitResult && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                  <button onClick={() => setFitResult(null)} style={btnStyle}>&larr; Back</button>
                  <h2 style={{ ...h2Style, marginBottom: 0 }}>Fit Results — {FIT_MODELS.find((m) => m.id === fitResult.model)?.label}</h2>
                </div>

                {/* Fitted parameters */}
                <div style={{ marginBottom: 16 }}>
                  <div style={sectionLabel}>Fitted Parameters</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 8 }}>
                    {fitResult.params && Object.entries(fitResult.params as Record<string, number>).map(([key, value]) => (
                      <div key={key} style={resultCardStyle}>
                        <div style={resultLabelStyle}>{key}</div>
                        <div style={resultValueStyle}>{(value as number).toPrecision(4)}</div>
                        {fitResult.units?.[key] && (
                          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{fitResult.units[key]}</div>
                        )}
                      </div>
                    ))}
                    {fitResult.r2 != null && (
                      <div style={resultCardStyle}>
                        <div style={resultLabelStyle}>R\u00B2</div>
                        <div style={resultValueStyle}>{(fitResult.r2 as number).toFixed(4)}</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Derived PK parameters */}
                {fitResult.derived && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={sectionLabel}>Derived PK Parameters</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 8 }}>
                      {Object.entries(fitResult.derived as Record<string, number>).map(([key, value]) => {
                        const labels: Record<string, string> = { ke: 'ke (1/h)', t_half: 't\u00BD (h)', AUC_inf: 'AUC\u221E (mg\u00B7h/L)', Cmax: 'Cmax (mg/L)', Tmax: 'Tmax (h)' };
                        return (
                          <div key={key} style={resultCardStyle}>
                            <div style={resultLabelStyle}>{labels[key] || key}</div>
                            <div style={resultValueStyle}>{(value as number).toPrecision(4)}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Fitted vs observed chart */}
                {fitResult.data && fitResult.fit && (
                  <div style={chartContainerStyle}>
                    <Plot
                      data={[
                        {
                          x: fitResult.data.t,
                          y: fitResult.data.c,
                          type: 'scatter' as const,
                          mode: 'markers' as const,
                          name: 'Observed',
                          marker: { color: '#f59e0b', size: 9, symbol: 'circle' },
                        },
                        {
                          x: fitResult.fit.t,
                          y: fitResult.fit.c,
                          type: 'scatter' as const,
                          mode: 'lines' as const,
                          name: 'Fitted',
                          line: { color: '#3b82f6', width: 2 },
                        },
                      ]}
                      layout={{
                        title: { text: 'Observed vs Fitted', font: { color: '#f5f5f5', size: 14 } },
                        xaxis: { title: { text: 'Time (h)', font: { color: '#a7abb6' } }, gridcolor: 'rgba(255,255,255,0.06)', tickfont: { color: '#a7abb6' } },
                        yaxis: { title: { text: 'Concentration (mg/L)', font: { color: '#a7abb6' } }, gridcolor: 'rgba(255,255,255,0.06)', tickfont: { color: '#a7abb6' } },
                        paper_bgcolor: 'transparent', plot_bgcolor: 'transparent',
                        legend: { font: { color: '#a7abb6' }, x: 0.7, y: 0.95 },
                        margin: { t: 40, r: 20, b: 50, l: 60 }, height: 380,
                      }}
                      config={{ displayModeBar: false, responsive: true }}
                      style={{ width: '100%' }}
                    />
                  </div>
                )}

                {/* Residuals */}
                {fitResult.residuals && (
                  <div style={chartContainerStyle}>
                    <Plot
                      data={[{
                        x: fitResult.residuals.t,
                        y: fitResult.residuals.y,
                        type: 'bar' as const,
                        name: 'Residuals',
                        marker: { color: (fitResult.residuals.y as number[]).map((v: number) => v >= 0 ? '#22c55e' : '#ef4444') },
                      }]}
                      layout={{
                        title: { text: 'Residuals', font: { color: '#f5f5f5', size: 14 } },
                        xaxis: { title: { text: 'Time (h)', font: { color: '#a7abb6' } }, gridcolor: 'rgba(255,255,255,0.06)', tickfont: { color: '#a7abb6' } },
                        yaxis: { title: { text: 'Residual (mg/L)', font: { color: '#a7abb6' } }, gridcolor: 'rgba(255,255,255,0.06)', tickfont: { color: '#a7abb6' }, zeroline: true, zerolinecolor: 'rgba(255,255,255,0.2)' },
                        paper_bgcolor: 'transparent', plot_bgcolor: 'transparent',
                        margin: { t: 40, r: 20, b: 50, l: 60 }, height: 240, showlegend: false,
                      }}
                      config={{ displayModeBar: false, responsive: true }}
                      style={{ width: '100%' }}
                    />
                  </div>
                )}

                {/* Actions */}
                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={() => {
                    const lines = ['parameter,value,unit'];
                    if (fitResult.params) Object.entries(fitResult.params).forEach(([k, v]) => lines.push(`${k},${v},${fitResult.units?.[k] || ''}`));
                    if (fitResult.r2 != null) lines.push(`R2,${fitResult.r2},`);
                    if (fitResult.derived) Object.entries(fitResult.derived).forEach(([k, v]) => lines.push(`${k},${v},`));
                    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a'); a.href = url; a.download = `pkpd_fit_${fitResult.model}.csv`; a.click(); URL.revokeObjectURL(url);
                  }} style={primaryBtnStyle}>Download CSV</button>
                  <button onClick={() => navigator.clipboard.writeText(JSON.stringify(fitResult, null, 2))} style={btnStyle}>Copy JSON</button>
                </div>
              </div>
            )}

          </div>

          {/* Sidebar */}
          <div style={{ width: 320, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <MethodCard
              engine={activeTab === 'simulate' ? 'mrgsolve' : 'SciPy'}
              title={activeTab === 'simulate'
                ? (selectedModel ? selectedModel.label : 'PK/PD Modelling')
                : 'Parameter Estimation'}
              description={activeTab === 'simulate'
                ? (selectedModel ? selectedModel.description : 'Compartmental pharmacokinetic modelling using mrgsolve. Simulate concentration-time profiles for various dosing regimens and PK models.')
                : 'Non-linear least-squares fitting of PK model parameters to observed concentration-time data. Estimates CL, V, KA and derives ke, t\u00BD, AUC.'}
              equation={activeTab === 'fit' ? fitModel.equation : undefined}
              parameters={activeTab === 'simulate'
                ? (selectedModel ? selectedModel.params.map((p) => ({ name: p.key, desc: `${p.label} (${p.unit})` })) : [
                    { name: 'mrgsolve', desc: 'ODE-based PK/PD in R' },
                    { name: 'Models', desc: `${MODELS.length} pre-configured` },
                    { name: 'Output', desc: 'Cp-time curve + PK params' },
                  ])
                : fitModel.params.map((p) => ({ name: p, desc: 'Estimated by fitting' }))}
              reference={activeTab === 'simulate' ? 'Baron et al. (2022) CPT:PSP 11:1416' : 'Virtanen et al. (2020) Nat Methods 17:261'}
              source={activeTab === 'simulate' ? 'mrgsolve R package' : 'scipy.optimize.curve_fit (Levenberg-Marquardt)'}
            />
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const statusBarStyle: React.CSSProperties = { padding: '8px 20px', background: 'var(--hover-bg)', borderBottom: '1px solid var(--card-border)', fontFamily: 'monospace', fontSize: 12, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 8 };
const h2Style: React.CSSProperties = { color: 'var(--text-primary)', fontSize: 18, fontWeight: 600, marginBottom: 12, marginTop: 0 };
const descStyle: React.CSSProperties = { color: 'var(--text-muted)', fontSize: 13, marginBottom: 16, lineHeight: 1.5 };
const btnStyle: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 13, cursor: 'pointer', fontWeight: 500 };
const primaryBtnStyle: React.CSSProperties = { ...btnStyle, background: '#f59e0b', color: 'var(--content-bg)', border: '1px solid #f59e0b', fontWeight: 600 };
const textareaStyle: React.CSSProperties = { width: '100%', padding: 14, background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10, color: 'var(--text-primary)', fontSize: 13, fontFamily: 'monospace', resize: 'vertical', outline: 'none', boxSizing: 'border-box' as const };
const selectStyle: React.CSSProperties = { padding: '4px 8px', background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 6, color: 'var(--text-primary)', fontSize: 13 };
const errorStyle: React.CSSProperties = { marginTop: 10, padding: '10px 14px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, color: '#fb7185', fontSize: 13, marginBottom: 16 };
const spinnerStyle: React.CSSProperties = { width: 20, height: 20, border: '3px solid var(--card-border)', borderTopColor: '#f59e0b', borderRadius: '50%', animation: 'spin 0.8s linear infinite' };
const sectionLabel: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 };
const modelCardStyle: React.CSSProperties = { background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10, padding: '14px 16px', cursor: 'pointer', textAlign: 'left', transition: 'border-color 200ms ease', width: '100%' };
const runBadge: React.CSSProperties = { fontSize: 11, padding: '3px 10px', borderRadius: 6, background: 'rgba(245,158,11,0.12)', color: '#f59e0b', fontWeight: 600, flexShrink: 0 };
const paramCard: React.CSSProperties = { background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 8, padding: '8px 12px', minWidth: 80 };
const resultCardStyle: React.CSSProperties = { background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10, padding: '10px 12px' };
const resultLabelStyle: React.CSSProperties = { color: 'var(--text-secondary)', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 };
const resultValueStyle: React.CSSProperties = { color: '#f59e0b', fontSize: 16, fontWeight: 700, fontFamily: 'monospace' };
const chartContainerStyle: React.CSSProperties = { background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10, padding: 16, marginBottom: 16 };

export default WorkbenchPKPD;
